import { useCallback } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router-dom';

import {
  Badge,
  ChakraFlex,
  ChakraTab,
  ChakraTabList,
  ChakraTabs,
  ChakraText,
  EquipUITheme,
} from '@equip.health/ui';

import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { TaskEvents } from '~/analytics/taskEvents';
import usePatientTaskCount from '~/lib/hooks/usePatientTaskCount';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import PatientTaskList from '~/components/maud/patients/profile/common/PatientTaskList';
import { TasksSubTab } from './constants';

const { label: labelStyle } = EquipUITheme.typography;

const TaskListView = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { trackEvent } = useAnalytics();

  const { maudTaskCount, familyTaskCount } = usePatientTaskCount(patientId);

  const subTabMatch = useRouteMatch<{ subTab: string }>(`${path}/:subTab`);
  const activeSubTab = subTabMatch?.params?.subTab;

  let selectedTabIndex = -1;
  if (activeSubTab === TasksSubTab.PATIENT_AND_SUPPORTS) {
    selectedTabIndex = 1;
  } else if (activeSubTab === TasksSubTab.EQUIP_STAFF) {
    selectedTabIndex = 0;
  }

  const handleTabChange = useCallback(
    (index: number) => {
      const subTab =
        index === 1
          ? TasksSubTab.PATIENT_AND_SUPPORTS
          : TasksSubTab.EQUIP_STAFF;

      const event =
        index === 1
          ? TaskEvents.patientAndSupportsTaskFilterSelectedFromTasksTabPanel
          : TaskEvents.equipStaffTaskFilterSelectedFromTasksTabPanel;

      trackEvent(event, {
        Source: AnalyticsSourceValues.TASKS_TAB,
        patientId,
        FilterName: index === 1 ? 'Patient and Supports' : 'Equip staff',
      });

      history.push(`${url}/${subTab}`);
    },
    [url, patientId, trackEvent],
  );

  const defaultSubTab =
    maudTaskCount === 0 && familyTaskCount > 0
      ? TasksSubTab.PATIENT_AND_SUPPORTS
      : TasksSubTab.EQUIP_STAFF;

  const tabs = [
    { count: maudTaskCount, label: 'Equip staff' },
    { count: familyTaskCount, label: 'Patient and Supports' },
  ];

  return (
    <>
      <ChakraFlex alignItems="center" marginTop="20px">
        <ChakraText
          color="neutral.secondary"
          marginRight="20px"
          {...labelStyle}
        >
          Show tasks for
        </ChakraText>
        <ChakraTabs
          index={selectedTabIndex >= 0 ? selectedTabIndex : undefined}
          isLazy
          onChange={handleTabChange}
          variant="soft-rounded"
        >
          <ChakraTabList
            background="neutral.background.secondary"
            borderRadius="29px"
            height="42px"
            id="patient-tab-list"
            padding="6px"
          >
            {tabs.map(({ count, label }, index) => (
              <ChakraTab
                _selected={{
                  backgroundColor: 'primary.100',
                  boxShadow: 'none',
                  color: 'white',
                }}
                color="neutral.secondary"
                id={`PATIENT_TASK_FILTER_TAB__${index}`}
                key={`tasks-filter-tab-${label}`}
                marginRight="8px"
                padding="6px 12px"
              >
                <ChakraText
                  {...labelStyle}
                  id={`PATIENT_TASK_FILTER_TAB__${index}-label`}
                  lineHeight="17px"
                >
                  {label}
                </ChakraText>
                {count > 0 && (
                  <Badge
                    badgeColor="tertiary.marigold.base"
                    fontSize="10px"
                    id="patient-menu__tasks"
                    margin="0 0 0 4px"
                  >
                    {count}
                  </Badge>
                )}
              </ChakraTab>
            ))}
          </ChakraTabList>
        </ChakraTabs>
      </ChakraFlex>

      <Switch>
        <Route exact path={`${path}/${TasksSubTab.EQUIP_STAFF}`}>
          <PatientTaskList
            isFamilyTask={false}
            patientId={patientId}
            subTabPath={TasksSubTab.EQUIP_STAFF}
          />
        </Route>
        <Route exact path={`${path}/${TasksSubTab.PATIENT_AND_SUPPORTS}`}>
          <PatientTaskList
            isFamilyTask
            patientId={patientId}
            subTabPath={TasksSubTab.PATIENT_AND_SUPPORTS}
          />
        </Route>
        <Redirect to={`${url}/${defaultSubTab}`} />
      </Switch>
    </>
  );
};

export default TaskListView;
